import { inject, Injectable } from '@angular/core';
import { pollUntilDone } from '@app/_shared/data/transactions/bulk-edit.utils';
import { RulesDataProviderService } from '@app/_shared/data/transactions/rules-data-provider.service';
import { RulesActions } from '@app/_store/features/rules/rules.actions';
import { selectPendingRuleSave } from '@app/_store/features/rules/rules.selectors';
import {
  isRuleConflictResponse,
  ruleConflictsOf,
  SaveRulePayload,
} from '@core/models/interfaces/storage-data/rules';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  race,
  switchMap,
  timer,
} from 'rxjs';

/** Matches the bulk-edit ceiling: applying a rule to existing rows can be slow. */
const SAVE_TIMEOUT_MS = 5 * 60 * 1000;

/** `pollUntilDone` errors with the server's raw `failReason`, which is a string. */
const failureReason = (error: unknown): string =>
  typeof error === 'string'
    ? error
    : ((error as Error)?.message ?? 'Something went wrong');

@Injectable()
export class RulesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private dataProvider = inject(RulesDataProviderService);

  saveRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RulesActions.saveRule),
      exhaustMap(({ payload }) => {
        const timeout$ = timer(SAVE_TIMEOUT_MS).pipe(
          map(() => RulesActions.saveRuleFailed({ reason: 'Timeout' }))
        );

        const process$ = this.dataProvider.saveRule(payload).pipe(
          switchMap((response) =>
            isRuleConflictResponse(response)
              ? of(
                  RulesActions.saveRuleConflicted({
                    conflicts: ruleConflictsOf(response),
                  })
                )
              : // No session means the rule was created with nothing to apply, and the
                // provider has already reloaded — there is nothing left to wait for.
                response.data == null
                ? of(RulesActions.saveRuleCompleted())
                : pollUntilDone(response.data, (editSessionId) =>
                    this.dataProvider.getRuleSaveStatus(editSessionId)
                  ).pipe(map(() => RulesActions.saveRuleCompleted()))
          ),
          catchError((error) =>
            of(RulesActions.saveRuleFailed({ reason: failureReason(error) }))
          )
        );

        return race(process$, timeout$);
      })
    )
  );

  /**
   * Re-sends the refused rule verbatim with `bypassConflicts`. It has to be the
   * stored payload rather than a rebuilt one so the `editSessionId` stays the
   * same, as the save contract requires.
   */
  confirmConflictedRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RulesActions.confirmConflictedRule),
      concatLatestFrom(() => this.store.select(selectPendingRuleSave)),
      filter(([, pendingSave]) => !!pendingSave),
      map(([, pendingSave]) =>
        RulesActions.saveRule({
          payload: {
            ...(pendingSave as SaveRulePayload),
            bypassConflicts: true,
          },
        })
      )
    )
  );

  deleteRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RulesActions.deleteRule),
      exhaustMap(({ id }) =>
        this.dataProvider.deleteRule(id).pipe(
          // A rejected body must not read as Completed — the panel closed
          // over a rule that was still there. The error interceptor raises
          // that body as an ApiError, so it lands in catchError below.
          map(() => RulesActions.deleteRuleCompleted()),
          catchError((error) =>
            of(RulesActions.deleteRuleFailed({ reason: failureReason(error) }))
          )
        )
      )
    )
  );

  /**
   * Fire and forget: the request's only visible effect is the red indicator
   * going away. A failure leaves the indicator on, which is the honest outcome —
   * it must not be reported as a rules error, because nothing the user asked for
   * has failed.
   */
  acknowledgeRule$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RulesActions.acknowledgeRule),
        mergeMap(({ id }) =>
          this.dataProvider.acknowledgeRule(id).pipe(catchError(() => EMPTY))
        )
      ),
    { dispatch: false }
  );
}
